import {Injectable} from '@angular/core';
import {Observable, of, ReplaySubject} from 'rxjs';
import {environment} from '../../../environments/environment';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {Favorite, FavoriteList} from '@models/favorite';
import {City, SquareLimit} from '@models/city';
import {CityService} from '@services/city.service';
import {EMPTY_CONTACT, EMPTY_FAVORITE, EMPTY_LOT} from '@models/empty';
import {AlertService} from '@services/alert.service';
import {AuthenticationService} from '@services/authentification.service';
import {UserSearch, UserSearchCity} from '@models/search';
import {User} from '@models/user';
// @ts-ignore
import {isNumber, isString} from 'util';
import {OnboardingService} from '@services/onboarding.service';

@Injectable({
    providedIn: 'root'
})
export class AdService {

    fav$ = new ReplaySubject<Favorite>();

    constructor(
        private http: HttpClient,
        private cityService: CityService,
        private alertService: AlertService,
        private onboardingService: OnboardingService,
        public authenticationService: AuthenticationService) {
    }

    updateRun = null;
    updateRunText = null;

    cacheFavorite(favorite: Favorite) {
        this.fav$ = new ReplaySubject<Favorite>();
        this.fav$.next(favorite);
    }

    updateCacheFavorite(favorite: Favorite) {
        this.fav$.next(favorite);
    }

    getCacheFavorite(): ReplaySubject<Favorite> {
        return this.fav$;
    }

    /**
     * Fonction for object
     */
    getEmptyFavorite(): Favorite {
        const favorite = Object.assign({}, EMPTY_FAVORITE);
        const user = this.authenticationService.currentUser;

        favorite.rooms = 1;
        favorite.agence = user.agence;
        favorite.amount_rent_insurance = user.amount_rent_insurance;
        favorite.apport = user.apport;
        favorite.duration = user.duration;
        favorite.building_insurance = user.building_insurance * (favorite.nb_lots > 1 ? favorite.nb_lots : 1);
        favorite.accouting_fees = user.accouting_fees;
        favorite.cfe_contribution = user.cfe_contribution;
        favorite.deposit_fees = user.deposit_fees;
        favorite.rate = user.rate;
        favorite.rate_second = user.rate + 0.5;
        favorite.rate_insurance = user.rate_insurance;
        favorite.banking_fees = user.banking_fees;
        favorite.shares_number = user.shares_number;
        favorite.annual_income = user.annual_income;
        favorite.annual_income_sci = user.annual_income_sci;
        favorite.fiscal_choice = user.fiscal_choice;

        return this.updateValues(favorite);
    }

    fillUSerInfo(favorite: Favorite): Favorite {
        const user = this.authenticationService.currentUser;

        favorite.agence = user.agence;
        favorite.amount_rent_insurance = user.amount_rent_insurance;
        if (user.apport_notary === 1) {
            favorite.apport = null;
        } else if (user.apport_notary === 0) {
            favorite.apport = user.apport;
        } else if (user.apport_notary === 2) {
            if (user.apport > 100) {
                user.apport = 100;
            }
            favorite.apport = user.apport / 100 * favorite.price;
        } else {
            favorite.apport = 0;
        }
        favorite.duration = user.duration;
        favorite.rate = user.rate;
        favorite.rate_second = user.rate + 0.5;
        favorite.rate_insurance = user.rate_insurance;
        favorite.banking_fees = user.banking_fees;
        favorite.shares_number = user.shares_number;
        favorite.annual_income = user.annual_income;
        favorite.annual_income_sci = user.annual_income_sci;
        favorite.fiscal_choice = user.fiscal_choice;
        favorite.building_insurance = user.building_insurance * (favorite.nb_lots > 1 ? favorite.nb_lots : 1);
        favorite.accouting_fees = user.accouting_fees;
        favorite.cfe_contribution = user.cfe_contribution;
        favorite.deposit_fees = user.deposit_fees;

        return this.updateValues(favorite);
    }

    fillDisplayData(favorite: Favorite, index): Favorite {
        // https://medium.com/showpad-engineering/why-you-should-never-use-function-calls-in-angular-template-expressions-e1a50f9c0496
        favorite.isSquareValid = this.isSquareValid(favorite);
        favorite.isAdVisible = this.isAdVisible(favorite, index);
        favorite.smallLink = this.findSmallLink(favorite.ad);
        if (favorite.ad.urls[favorite.smallLink]) {
            favorite.providerLib = this.getProviderLib(favorite.ad.urls[favorite.smallLink].provider);
        }

        favorite.fullPriceLib = this.cityService.getFullLib(favorite.advice_price_real, favorite.ad_city, favorite.iris);
        favorite.quartierLib = this.cityService.getQuartierName('_gq', favorite.ad_city, favorite.ad.iris.substring(0, 7)) + ' ' + this.cityService.getQuartierName('_iris', favorite.ad_city, favorite.ad.iris);

        return favorite;
    }

    cleanForImpot(favorite: Favorite, user: User) {
        let fav: any;

        fav = Object.assign({}, favorite);
        fav.annual_rent_increase = fav.annual_rent_increase / 100;
        fav.annual_bill_increase = fav.annual_bill_increase / 100;
        fav.agence = fav.agence / 100;
        fav.amount_rent_insurance = fav.amount_rent_insurance / 100;
        fav.rate = fav.rate / 100;
        fav.rate_insurance = fav.rate_insurance / 100;
        fav.rental_vacancy = fav.rental_vacancy / 12;
        // User amortissement
        fav.amort_duration_furniture = user.amort_duration_furniture;
        fav.amort_duration_agency_fees = user.amort_duration_agency_fees;
        fav.amort_duration_work = user.amort_duration_work;
        fav.amort_duration_structure = user.amort_duration_structure;
        fav.amort_duration_facade = user.amort_duration_facade;
        fav.amort_duration_arrangment = user.amort_duration_arrangment;
        fav.amort_duration_equipment = user.amort_duration_equipment;
        fav.part_structure = user.part_structure / 100;
        fav.part_facade = user.part_facade / 100;
        fav.part_arrangment = user.part_arrangment / 100;
        fav.part_equipment = user.part_equipment / 100;
        fav.part_field = user.part_field / 100;
        fav.other_fees = fav.other_fees + fav.other_fees_work;

        if (fav.selling_price === 0) {
            fav.selling_price = fav.price;
        }

        if (fav.shares_number === 0) {
            fav.shares_number = 1;
        }

        if (fav.fiscal_choice == 'Societe IS') {
            fav.fiscal_choice = 'SCI IS';
        }

        if (fav.selling == false) {
            fav.selling_year = 0;
        }

        fav.denormandie_work_price = fav.available_work_price;
        fav.year_pinel = fav.buying_year;

        return fav;
    }

    // Transform input text to number
    cleanInput(fav: Favorite) {
        const model = Object.assign({}, EMPTY_FAVORITE);

        Object.keys(model).forEach(key => {
            if (typeof model[key] === 'number') {
                if (isNaN(fav[key])) {
                    fav[key] = 0;
                } else if (typeof fav[key] === 'undefined') {
                    fav[key] = 0;
                } else if (typeof fav[key] === 'string') {
                    if (fav[key] === '') {
                        fav[key] = 0;
                    } else {
                        fav[key] = parseFloat(fav[key]);
                    }
                }
            }
        });
        fav.price = Math.round(fav.price);
        fav.square = Math.round(fav.square);
        fav.rooms = Math.round(fav.rooms);
        fav.amount_copro = Math.round(fav.amount_copro);
        fav.amount_copro_rent = Math.round(fav.amount_copro_rent);
        fav.amount_property_tax = Math.round(fav.amount_property_tax);
        fav.amount_rent = Math.round(fav.amount_rent);
        fav.works_total = Math.round(fav.works_total);

        const model2 = EMPTY_LOT;
        if (fav.lots) {
            fav.lots.forEach((lots, index) => {
                lots.amount_rent = Math.round(lots.amount_rent);
                lots.square = Math.round(lots.square);
                lots.amount_copro_rent = Math.round(lots.amount_copro_rent);
                lots.works_total = Math.round(lots.works_total);

                Object.keys(model2).forEach(key => {
                    if (isNumber(model2[key]) && isString(fav.lots[index][key])) {
                        if (fav.lots[index][key] === '') {
                            fav.lots[index][key] = 0;
                        } else {
                            fav.lots[index][key] = parseFloat(fav.lots[index][key]);
                        }
                    }
                });
            });
        }

        return fav;
    }

    sortGeneralFirst(a, b) {
        if (a.key == 'Général') {
            return 0;
        } else if (b.key == 'Général') {
            return 1;
        }
        return (b.key[0] > a.key[0] ? 0 : 1);
    }

    updateValues(fav: Favorite): Favorite {

        if (fav.advice_price_choice == 'city.prix_commune_all') {
            fav.advice_price_choice = 'city.prix_commune.B';
        }

        if (fav.carac_filled == false || fav.carac_filled == undefined) {
            // Caractéristiques
            fav.has_copro = fav.ad.has_copro;
            fav.lot_copro = fav.ad.lot_copro;
            fav.floor = fav.ad.floor;
            // fav.floor_building = fav.ad.floor_building;
            fav.year_building = fav.ad.year_building;
            fav.orientation = fav.ad.orientation;

            // Diag
            fav.energy_rate = fav.ad.energy_rate;
            fav.energy_rate_value = fav.ad.energy_rate_value;
            fav.ges = fav.ad.ges;
            fav.ges_value = fav.ad.ges_value;

            // Surfaces annexes
            fav.bedrooms = fav.ad.bedrooms;
            fav.bathrooms = fav.ad.bathrooms;
            fav.shower_rooms = fav.ad.shower_rooms;
            fav.restrooms = fav.ad.restrooms;
            fav.parking = fav.ad.parking;
            fav.cellar = fav.ad.cellar;
            fav.pool = fav.ad.pool;
            fav.outside = fav.ad.outside;

            // Autre
            fav.elevator = fav.ad.elevator;
            fav.urgent = fav.ad.urgent;
            fav.rent = fav.ad.rent;
            fav.work = fav.ad.work;
            fav.renov = fav.ad.renov;
            fav.owner_type = fav.ad.owner_type;
            fav.disabled_access = fav.ad.disabled_access;
            fav.guardian = fav.ad.guardian;
            fav.intercom = fav.ad.intercom;
            fav.closed_view = fav.ad.closed_view;
            fav.digital_code = fav.ad.digital_code;

            fav.carac_filled = true;
        }

        if (fav.iris == fav.ad_city.city_code + '0000') {
            fav.iris = '';
        }
        const user = this.authenticationService.currentUser;
        if (fav.works_help !== -1
            && fav.works_square_price !== 0
            && fav.works_square_price !== user.work_1
            && fav.works_square_price !== user.work_2
            && fav.works_square_price !== user.work_3) {
            fav.works_help = -1;
        }
        if (fav.lots) {
            if (fav.lots.length > 1) {
                fav.nb_lots = fav.lots.length;
            }
            fav.lots.forEach(lot => {
                if (lot.works_help !== -1
                    && lot.works_square_price !== 0
                    && lot.works_square_price !== user.work_1
                    && lot.works_square_price !== user.work_2
                    && lot.works_square_price !== user.work_3) {
                    lot.works_help = -1;
                }
                if (lot.furnish == 3) {
                    lot.amount_rent = lot.night_number * lot.night_price;
                }
            });
        }
        if (fav.fiscal_choice == '') {
            fav.fiscal_choice = user.fiscal_choice;
        }

        if (fav.nb_lots === 0) {
            fav.nb_lots = 1;
        } else if (fav.lots !== null) {
            fav.lots.forEach(lot => {
                if (lot.nb_lots === 0) {
                    lot.nb_lots = 1;
                }
            });
        }

        if (fav.company === false) {
            fav.accouting_fees = 0;
            fav.cfe_contribution = 0;
            fav.cga_contribution = 0;
        }

        if (fav.notary_price === null || fav.notary_price === undefined) {
            if (fav.estate_sale === 'Neuf') {
                fav.notary_price = Math.round(fav.price * 0.025);
            } else {
                fav.notary_price = Math.round(fav.price * 0.08);
            }
        }

        if (fav.apport === null) {
            fav.apport = fav.notary_price;
        }
        if (fav.apport === undefined) {
            fav.apport = 0;
        }
        if (fav.duration === null || fav.duration === undefined) {
            fav.duration = 0;
        }
        if (fav.rate === null || fav.rate === undefined) {
            fav.rate = 0;
        }
        if (fav.rate_insurance === null || fav.rate_insurance === undefined) {
            fav.rate_insurance = 0;
        }
        if (fav.agence === null || fav.agence === undefined) {
            fav.agence = 0;
        }
        if (fav.other_bill === null || fav.other_bill === undefined) {
            fav.other_bill = 0;
        }
        if (fav.amount_copro === null || fav.amount_copro === undefined) {
            fav.amount_copro = 0;
        }
        if (fav.amount_copro_rent === null || fav.amount_copro_rent === undefined) {
            fav.amount_copro_rent = 0;
        }
        if (fav.amount_rent_insurance === null || fav.amount_rent_insurance === undefined) {
            fav.amount_rent_insurance = 0;
        }
        if (fav.rental_vacancy === null || fav.rental_vacancy === undefined) {
            fav.rental_vacancy = 0;
        }

        if (fav.lots == null) {
            fav.lots = [Object.assign({}, EMPTY_LOT)];
        }
        if (fav.contacts == null) {
            fav.contacts = [Object.assign({}, EMPTY_CONTACT)];
        }

        /**
         * TRAVAUX
         */
        if (fav.works_help === null || fav.works_help === undefined) {
            fav.works_help = 0;
            fav.works_square_price = 0;
            fav.works_total = 0;
        } else if (fav.works_help !== -1) {
            fav.works_square_price = fav.works_help;
            fav.works_total = fav.works_square_price * fav.square;
        } else if (fav.works_total == null) {
            fav.works_total = fav.works_square_price * fav.square;
        }

        if (fav.square !== 0) {
            fav.works_square_price = Math.round(fav.works_total / fav.square);
        } else {
            fav.works_square_price = 0;
        }

        if (fav.advice_price_choice == '') {
            if (fav.estate_type == 'Parking') {
                fav.advice_price_choice = 'city.prix_commune.P';
            } else if (fav.estate_type == 'Terrain') {
                fav.advice_price_choice = 'city.prix_commune.T';
            } else if (fav.estate_type == 'Appartement' || fav.estate_type == 'Immeuble') {
                fav.advice_price_choice = 'city.prix_commune.A';
            } else if (fav.estate_type == 'Maison') {
                fav.advice_price_choice = 'city.prix_commune.M';
            } else {
                fav.advice_price_choice = 'city.prix_commune.B';
            }
        }

        fav.advice_price_choice = fav.advice_price_choice.replace('_gq', '');
        fav.advice_price_choice = fav.advice_price_choice.replace('_iris', '');
        if (fav.iris.length == 7) {
            let s = fav.advice_price_choice.split('.');
            fav.advice_price_choice = s[0] + '.' + s[1] + '_gq.' + s[2];
        } else if (fav.iris.length > 7) {
            let s = fav.advice_price_choice.split('.');
            fav.advice_price_choice = s[0] + '.' + s[1] + '_iris.' + s[2];
        }

        let p = this.cityService.getPriceValue(fav.ad_city, fav.advice_price_choice, fav.iris);
        if (p.pM > 0) {
            fav.advice_price = p.pM;
            fav.advice_price_L = p.q25;
            fav.advice_price_U = p.q75;
            fav.advice_price_real = fav.advice_price_choice;
            fav.advice_price_cL = p.cL;
        } else {
            let p = this.cityService.getFallbackPriceValue(fav.ad_city, fav.advice_price_choice, fav.iris);
            fav.advice_price = p[0].pM;
            fav.advice_price_L = p[0].q25;
            fav.advice_price_U = p[0].q75;
            fav.advice_price_real = p[1];
            fav.advice_price_cL = p[0].cL;
        }

        if (fav.advice_rent_choice == '') {
            if (fav.estate_type == 'Parking') {
                fav.advice_rent_choice = 'city.loyer_moyen_ville.P';
            } else if (fav.estate_type == 'Appartement' || fav.estate_type == 'Immeuble') {
                fav.advice_rent_choice = 'city.loyer_moyen_ville.A';
            } else if (fav.estate_type == 'Maison') {
                fav.advice_rent_choice = 'city.loyer_moyen_ville.M';
            } else {
                fav.advice_rent_choice = 'city.loyer_moyen_petite_ville_cp';
            }
        }

        if (fav.advice_rent_choice == 'city.loyer_moyen_ville.C') {
            fav.furnish = 4;
            fav.lots.forEach(lot => {
                if (lot.advice_rent_choice == 'city.loyer_moyen_ville.C') {
                    lot.furnish = 4;
                }
            });
        }

        p = this.cityService.getRentValue(fav.ad_city, fav.advice_rent_choice);
        if (p) {
            let square = fav.square;
            if (square == 0) {
                square = p.s_moy;
            }
            if (fav.furnish == 0 || fav.furnish == 1 || fav.furnish == 2) {
                fav.advice_rent = p.x0 * fav.nb_lots + p.x1 * square;
                fav.advice_rent_L = p.x0_l * fav.nb_lots + p.x1_l * square;
                fav.advice_rent_U = p.x0_u * fav.nb_lots + p.x1_u * square;
            } else if (fav.furnish == 4) {
                fav.advice_rent = p.x0 * fav.rooms;
                fav.advice_rent_L = p.x0_l * fav.rooms;
                fav.advice_rent_U = p.x0_u * fav.rooms;
            }

            fav.advice_rent_real = fav.advice_rent_choice;
            fav.advice_rent_square = square;
            fav.advice_rent_cL = p.cL;
        } else {
            let p = this.cityService.getFallbackRentValue(fav.ad_city, fav.advice_rent_choice);
            let square = fav.square;
            if (square == 0) {
                square = p[0].s_moy;
            }
            if (fav.furnish == 0 || fav.furnish == 1 || fav.furnish == 2) {
                fav.advice_rent = p[0].x0 * fav.nb_lots + p[0].x1 * square;
                fav.advice_rent_L = p[0].x0_l * fav.nb_lots + p[0].x1_l * square;
                fav.advice_rent_U = p[0].x0_u * fav.nb_lots + p[0].x1_u * square;
            } else if (fav.furnish == 4) {
                fav.advice_rent = p[0].x0 * fav.rooms;
                fav.advice_rent_L = p[0].x0_l * fav.rooms;
                fav.advice_rent_U = p[0].x0_u * fav.rooms;
            }

            fav.advice_rent_real = p[1];
            fav.advice_rent_square = square;
            fav.advice_rent_cL = p[0].cL;
        }

        /**
         * LOTS
         */
        if (fav.lots.length === 1) {

            fav.lots[0].square = fav.square;
            fav.lots[0].amount_rent = fav.amount_rent;
            fav.lots[0].amount_copro_rent = fav.amount_copro_rent;
            fav.lots[0].nb_lots = fav.nb_lots;

            fav.lots[0].advice_rent_choice = fav.advice_rent_choice;

            fav.lots[0].works_help = fav.works_help;
            fav.lots[0].works_square_price = fav.works_square_price;
            fav.lots[0].works_total = fav.works_total;

            fav.lots[0].advice_rent = fav.advice_rent;
            fav.lots[0].advice_rent_L = fav.advice_rent_L;
            fav.lots[0].advice_rent_U = fav.advice_rent_U;
            fav.lots[0].advice_rent_cL = fav.advice_rent_cL;

        } else {
            fav.amount_rent = 0;
            fav.works_total = 0;
            fav.amount_copro_rent = 0;
            fav.square = 0;

            fav.lots.forEach(lot => {
                lot.nb_lots = 1;
                if (lot.works_help === null || lot.works_help === undefined) {
                    lot.works_help = 0;
                    lot.works_square_price = 0;
                    lot.works_total = 0;
                } else if (lot.works_help !== -1) {
                    lot.works_square_price = lot.works_help;
                    lot.works_total = lot.works_square_price * lot.square;
                } else if (lot.works_total == null) {
                    lot.works_total = lot.works_square_price * lot.square;
                }

                if (lot.advice_rent_choice === '' && lot.estate_type !== '' && lot.rooms > 0) {
                    lot.advice_rent_choice = 'city.loyer_moyen_ville_nb_piece[\'' + lot.rooms + '\']';
                } else if (lot.estate_type === 'Parking') {
                    lot.advice_rent_choice = 'city.loyer_moyen_ville.P';
                }

                p = this.cityService.getRentValue(fav.ad_city, lot.advice_rent_choice);
                if (p) {
                    if (fav.furnish == 0 || lot.furnish == 1 || lot.furnish == 2) {
                        lot.advice_rent = p.x0 * lot.nb_lots + p.x1 * lot.square;
                        lot.advice_rent_L = p.x0_l * lot.nb_lots + p.x1_l * lot.square;
                        lot.advice_rent_U = p.x0_u * lot.nb_lots + p.x1_u * lot.square;
                    } else if (fav.furnish == 4) {
                        lot.advice_rent = p.x0 * lot.rooms;
                        lot.advice_rent_L = p.x0_l * lot.rooms;
                        lot.advice_rent_U = p.x0_u * lot.rooms;
                    }
                    lot.advice_rent_cL = p.cL;
                }

                lot.works_square_price = Math.round(lot.works_total / lot.square);

                if (lot.amount_rent) {
                    fav.amount_rent += Math.round(lot.amount_rent);
                }
                if (lot.works_total) {
                    fav.works_total += Math.round(lot.works_total);
                }
                if (lot.square) {
                    fav.square += Math.round(lot.square);
                }
                if (lot.amount_copro_rent) {
                    fav.amount_copro_rent += Math.round(lot.amount_copro_rent);
                }
            });
        }

        /**
         * Chasseur OIKOS
         */
        if (user.pro_plan == 'oikos') {
            if (fav.other_fees === null) {
                fav.other_fees = Math.round(fav.price * 0.08);
                if (fav.other_fees < 10000) {
                    fav.other_fees = 10000;
                }
            }

            if (fav.other_fees_work === null) {
                fav.other_fees_work = Math.round(fav.works_total * 0.08);
                if (fav.other_fees_work < 3000 && fav.works_total > 0) {
                    fav.other_fees_work = 3000;
                }
            }

            if (fav.building_insurance === null) {
                fav.building_insurance = fav.square * 2;
            }
        } else if (user.pro_plan == 'minuta') {
            if (fav.other_fees === null) {
                fav.other_fees = Math.round(fav.price * 0.07);
                if (fav.other_fees < 8000) {
                    fav.other_fees = 8000;
                }
            }

            if (fav.other_fees_work === null) {
                fav.other_fees_work = 0;
            }
            if (fav.building_insurance === undefined || fav.building_insurance === null) {
                fav.building_insurance = 0;
            }
        } else {
            if (fav.other_fees === null) {
                fav.other_fees = 0;
            }
            if (fav.other_fees_work === null) {
                fav.other_fees_work = 0;
            }
            if (fav.building_insurance === undefined || fav.building_insurance === null) {
                fav.building_insurance = 0;
            }
        }

        if (fav.description === '') {
            fav.description = fav.ad.description;
        }
        if (fav.pictures_string === '') {
            fav.pictures_string = fav.ad.pictures_string;
        }
        if (fav.pictures_string !== undefined && fav.pictures_string !== '') {
            fav.pictures = fav.pictures_string.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').split(';');
        } else {
            fav.pictures = ['/assets/no-photo.png'];
        }

        if (fav.ad && fav.ad.pictures_string !== undefined && fav.ad.pictures_string !== '') {
            fav.ad.pictures = fav.ad.pictures_string.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').split(';');
        } else {
            fav.ad.pictures = ['/assets/no-photo.png'];
        }

        fav = this.cleanInput(fav);
        /**
         * CALCUL
         */
        fav.total_price = fav.price + fav.other_fees + fav.other_fees_work + fav.notary_price + fav.works_total + fav.works_not_deduc + fav.furniture_price + fav.banking_fees + fav.deposit_fees;

        /**
         * Verification
         */
        if (fav.apport > fav.total_price) {
            fav.apport = fav.total_price;
        }

        // Emprunt total
        fav.loaning = fav.total_price - fav.apport;

        // Mensualite credit
        if (fav.duration == 0) {
            fav.monthly = 0;
        } else if (fav.rate === 0) {
            fav.monthly = Math.round(fav.loaning / (fav.duration * 12) + fav.rate_insurance * (fav.loaning / 100) / 12);
        } else {
            fav.monthly = Math.round(
                (fav.loaning * ((fav.rate / 100) / 12)) / (1 - Math.pow(1 + ((fav.rate / 100) / 12), -1 * (fav.duration * 12)))
                + fav.rate_insurance * (fav.loaning / 100) / 12);
        }

        if (fav.monthly < 0) {
            fav.monthly = 0;
        }

        if (fav.selling_price_calc == true) {
            fav.selling_price = Math.round((fav.price + fav.works_total) * Math.pow(1 + (fav.selling_price_evol / 100), fav.selling_year));
        }

        // Taux de couverture
        fav.coverage_rate = fav.monthly / fav.amount_rent * 100;

        const amount_rental_vacancy = (fav.amount_rent + fav.amount_copro_rent) * fav.rental_vacancy / 12;
        const real_amount_rent = fav.amount_rent - amount_rental_vacancy + fav.amount_copro_rent;

        // Loyer moins toutes les charges
        fav.charge = (fav.amount_property_tax / 12)
            + (fav.building_insurance / 12)
            + amount_rental_vacancy
            + (real_amount_rent * fav.agence / 100)
            + fav.agence_rental / 12
            + (real_amount_rent * fav.amount_rent_insurance / 100)
            + (fav.other_bill / 12)
            + (fav.amount_copro / 12)
            + (fav.accouting_fees / 12)
            + (fav.cfe_contribution / 12)
            + (fav.cga_contribution / 12)
        ;

        fav.net_rent = fav.amount_rent - fav.charge;
        fav.cash_flow = Math.round(fav.net_rent - fav.monthly);

        if (fav.total_price === 0) {
            fav.efficiency_brut = 0;
            fav.efficiency_net = 0;
        } else {
            fav.efficiency_brut = Math.round((fav.amount_rent * 12) * 100 / fav.total_price * 10) / 10;
            fav.efficiency_net = Math.round((fav.net_rent * 12) * 100 / fav.total_price * 10) / 10;
        }

        return fav;
    }

    saveCity(favorite): Observable<City> {
        return this.http.put<City>(environment.apiUrl + 'app/favorite/' + favorite.id + '/city/' + favorite.ad_city.city_code, {});
    }

    moveFavorite(favoriteListId, favorite: Favorite): Observable<Favorite> {
        return this.http.put<Favorite>(environment.apiUrl + 'app/favorite/' + favorite.id + '/favoriteLists/' + favoriteListId, {});
    }

    deleteFavorite(favorite: Favorite): Observable<any> {
        return this.http.delete<any>(environment.apiUrl + 'app/favorite/' + favorite.id, {});
    }

    getFavorite(id): Observable<Favorite> {
        return this.http.get<Favorite>(environment.apiUrl + 'app/favorite/' + id);
    }

    getshare(id): Observable<Favorite> {
        return this.http.get<Favorite>(environment.apiUrl + 'app/share/' + id);
    }

    addAnnonce(favorite: Favorite): Observable<Favorite> {
        const fav = Object.assign({}, favorite);
        fav.favorite_list = null;
        fav.ad = null;
        fav.ad_city.prix_departement = null;
        fav.ad_city.prix_ville = null;
        fav.ad_city.service = null;
        fav.ad_city.commerce = null;
        fav.ad_city.ecole = null;
        fav.ad_city.logement = null;
        fav.ad_city.menage = null;
        fav.ad_city.tourisme = null;
        fav.ad_city.ets = null;
        fav.ad_city.renta = null;
        return this.http.post<Favorite>(environment.apiUrl + 'lybox/process', fav);
    }

    updateFavorite(favorite: Favorite) {

        if (favorite.id === '') {
            return;
        }
        if (this.updateRun != null) {
            clearTimeout(this.updateRun);
        }

        $('#typing').fadeIn();
        $('#saved').hide();

        const t = this;
        this.updateRun = setTimeout(() => {
            favorite = this.cleanInput(favorite);
            favorite.pictures_string = favorite.pictures.join(';');

            this.onboardingService.endAnnonce(1, 1);

            this.http.put(environment.apiUrl + 'app/favorite/' + favorite.id, favorite).subscribe(result => {
                $('#typing').hide();
                $('#saved').fadeIn();
                setTimeout(() => {
                    $('#saved').fadeOut();
                }, 3000);
                console.log('Success update');
            }, error => {
                this.alertService.error('Une erreur est survenue lors de l\'enregistrement.');
                console.log(error);
            });
        }, 1000);
    }

    updateFavoriteText(favorite: Favorite, text: string, column: string) {

        if (favorite.id === '') {
            return;
        }
        if (this.updateRunText != null) {
            clearTimeout(this.updateRunText);
        }

        $('#typing').fadeIn();
        $('#saved').hide();

        const t = this;
        this.updateRunText = setTimeout(() => {

            this.http.put(environment.apiUrl + 'app/favorite/' + favorite.id + '/text', {column, text}).subscribe(result => {
                $('#typing').hide();
                $('#saved').fadeIn();
                setTimeout(() => {
                    $('#saved').fadeOut();
                }, 3000);
                console.log('Success update');
            }, error => {
                this.alertService.error('Une erreur est survenue lors de l\'enregistrement.');
                console.log(error);
            });
        }, 1000);
    }

    sendBug(favorite, bug, comment): Observable<any> {
        return this.http.post<Favorite>(environment.apiUrl + 'app/favorite/bug', {
            favorite: favorite,
            bug: bug,
            comment: comment,
        });
    }

    createFavorites(favorite, list = ''): Observable<Favorite> {
        const fav = Object.assign({}, favorite);
        fav.ad_id = favorite.ad_id;
        fav.ad = null;
        fav.ad_city = null;

        if (list === '') {
            return this.http.post<Favorite>(environment.apiUrl + 'app/favorite', fav);
        } else {
            return this.http.post<Favorite>(environment.apiUrl + 'app/favorite/' + list, fav);
        }
    }

    openAd(id): Observable<Favorite> {
        return this.http.get<Favorite>(environment.apiUrl + 'app/create/' + id);
    }

    openURL(url): Observable<HttpResponse<Favorite>> {
        return this.http.post<Favorite>(environment.apiUrl + 'app/create', {url}, {observe: 'response'});
    }

    hideFav(favorite: Favorite) {
        this.http.post<Favorite>(environment.apiUrl + 'app/search/hide', {hide: favorite.hide, id: favorite.ad.ai_id}, {observe: 'response'}).subscribe(result => {
        }, error => {
        });
    }

    dupplicateFavorites(favorite): Observable<Favorite> {
        return this.http.post<Favorite>(environment.apiUrl + 'app/favorite/' + favorite.id + '/dupplicate', {
            title: favorite.title,
        });
    }

    addFavoritesList(favoriteList: FavoriteList): Observable<any> {
        return this.http.post<any>(environment.apiUrl + 'app/favoriteLists', favoriteList);
    }

    sortFavoritesList(favoriteList: FavoriteList[]): Observable<any> {
        return this.http.post<any>(environment.apiUrl + 'app/favoriteLists/sort', favoriteList);
    }

    editFavoritesList(favoriteList: FavoriteList): Observable<FavoriteList> {
        return this.http.put<FavoriteList>(environment.apiUrl + 'app/favoriteLists/' + favoriteList.id, favoriteList);
    }

    getFavoritesList(FavoriteListId): Observable<FavoriteList> {
        return this.http.get<FavoriteList>(environment.apiUrl + 'app/favoriteLists/' + FavoriteListId);
    }

    getFavoritesListShare(FavoriteListId): Observable<any> {
        return this.http.get<any>(environment.apiUrl + 'app/favoriteLists/' + FavoriteListId + '/share');
    }

    favoritesListShare(FavoriteListId, params): Observable<any> {
        return this.http.post<any>(environment.apiUrl + 'app/favoriteLists/' + FavoriteListId + '/share', params);
    }

    deleteFavoriteList(FavoriteListId): Observable<any> {
        return this.http.delete<any>(environment.apiUrl + 'app/favoriteLists/' + FavoriteListId);
    }

    getFavoritesLists(): Observable<FavoriteList[]> {
        return this.http.get<FavoriteList[]>(environment.apiUrl + 'app/favoriteLists');
    }

    search(params): Observable<any> {
        return this.http.post<any>(environment.apiUrl + 'app/search_es', params);
    }

    searchCities(params): Observable<any> {
        return this.http.post<any>(environment.apiUrl + 'app/search_cities', params);
    }

    getUserSearch(): Observable<UserSearch[]> {
        return this.http.get<UserSearch[]>(environment.apiUrl + 'app/user/search');
    }

    getUserSearchCity(): Observable<UserSearchCity[]> {
        return this.http.get<UserSearchCity[]>(environment.apiUrl + 'app/user/searchCity');
    }

    addUserSearch(userSearch: UserSearch): Observable<UserSearch> {
        return this.http.post<UserSearch>(environment.apiUrl + 'app/user/search', userSearch);
    }

    addUserSearchCity(userSearch: UserSearchCity): Observable<UserSearchCity> {
        return this.http.post<UserSearchCity>(environment.apiUrl + 'app/user/searchCity', userSearch);
    }

    editUserSearch(userSearch): Observable<UserSearch> {
        let s = Object.assign({}, userSearch);
        if (s.search_ad_params.surface == null) {
            s.search_ad_params.surface = [0];
        }
        if (s.search_ad_params.surface_land == null) {
            s.search_ad_params.surface_land = [0];
        }
        if (s.search_ad_params.surface.length == 2) {
            s.search_ad_params.surface[1] = parseInt(s.search_ad_params.surface[1]);
        }
        if (s.search_ad_params.surface_land.length == 2) {
            s.search_ad_params.surface_land[1] = parseInt(s.search_ad_params.surface_land[1]);
        }
        return this.http.put<UserSearch>(environment.apiUrl + 'app/user/search/' + userSearch.id, s);
    }

    editUserSearchCity(userSearch: UserSearchCity): Observable<UserSearchCity> {
        return this.http.put<UserSearchCity>(environment.apiUrl + 'app/user/searchCity/' + userSearch.id, userSearch);
    }

    editUserSearchAlert(userSearch: UserSearch): Observable<UserSearch> {
        return this.http.put<UserSearch>(environment.apiUrl + 'app/user/search/' + userSearch.id + '/alert', userSearch);
    }

    deleteSearch(userSearch: UserSearch): Observable<any> {
        return this.http.delete<any>(environment.apiUrl + 'app/user/search/' + userSearch.id);
    }

    deleteSearchCity(userSearch: UserSearchCity): Observable<any> {
        return this.http.delete<any>(environment.apiUrl + 'app/user/searchCity/' + userSearch.id);
    }

    getCompareAd(): Observable<string[]> {
        const str = localStorage.getItem('lybox_compareAd');
        if (str === '' || str === null) {
            return of([]);
        }
        const cols = JSON.parse(str) as string[];

        return of(cols);
    }

    addCompareAd(favoriteId) {
        this.getCompareAd().subscribe(ads => {
            const index = ads.indexOf(favoriteId, 0);
            if (index === -1) {
                ads.push(favoriteId);
                localStorage.setItem('lybox_compareAd', JSON.stringify(ads));
            }
        });
    }

    deleteCompareAd(favoriteId) {
        this.getCompareAd().subscribe(ads => {
            const index = ads.indexOf(favoriteId, 0);
            if (index > -1) {
                ads.splice(index, 1);
                localStorage.setItem('lybox_compareAd', JSON.stringify(ads));
            }
        });
    }

    setMainCompareAd(favoriteId) {
        this.getCompareAd().subscribe(ads => {
            const index = ads.indexOf(favoriteId, 0);
            if (index > -1) {
                ads.splice(index, 1);
            }
            ads.unshift(favoriteId);
            localStorage.setItem('lybox_compareAd', JSON.stringify(ads));
        });
    }

    getProviderLib(slug) {
        switch (slug) {
            case 'leboncoin' :
                return 'Leboncoin';
                break;
            case'bellesdemeures' :
                return 'Belles Demeures';
                break;
            case'gensdeconfiance' :
                return 'Gens de confiance';
                break;
            case 'seloger' :
                return 'SeLoger';
                break;
            case 'logicimmo' :
                return 'Logic-immo';
                break;
            case 'avendrealouer' :
                return 'Avendrealouer';
                break;
            case 'immonotaires' :
                return 'Immo Notaires';
                break;
            case 'fnaim' :
                return 'FNAIM';
                break;
            case'bienici' :
                return 'Bien\'ici';
                break;
            case'laforet' :
                return 'La forêt';
                break;
            case'capifrance' :
                return 'Capifrance';
                break;
            case'citya' :
                return 'Citya';
                break;
            case'guyhoquet' :
                return 'Guy Hoquet';
                break;
            case'aucoeurdelimmo' :
                return 'Au coeur de l\'immo';
                break;
            case'stephaneplaza' :
                return 'Stéphane Plaza';
                break;
            case 'orpi' :
                return 'Orpi';
                break;
            case 'century21' :
                return 'Century 21';
                break;
            case 'iad' :
                return 'IAD';
                break;
            case 'efficity' :
                return 'efficity';
                break;
            case 'foncia' :
                return 'Foncia';
                break;
            case 'pap' :
                return 'PAP';
                break;
            case 'hektor' :
                return 'Site partenaire';
                break;
            case 'partagimmo' :
                return 'Site partenaire';
                break;
            case 'win' :
                return 'Site partenaire';
                break;
            case 'adaptimmo' :
                return 'Site partenaire';
                break;
            case 'netty' :
                return 'Site partenaire';
                break;
            case 'ubiflow' :
                return 'Site partenaire';
                break;
            case 'apimo' :
                return 'Site partenaire';
                break;
            case 'figimmo' :
                return 'Le figaro';
                break;
            case 'ouestfrance' :
                return 'Ouest France';
                break;
            case 'squarehabitat' :
                return 'Square habitat';
                break;
            case 'bskimmobilier' :
                return 'BSK Immobilier';
                break;
            case 'nestenn' :
                return 'Nestenn';
                break;
            case 'human' :
                return 'HUMAN Immobilier';
                break;
            case 'safti' :
                return 'Safti';
                break;
            case 'maisonsetappartements' :
                return 'Maisons et appartements';
                break;
            case 'entreparticuliers':
                return 'Entre particuliers';
                break;
            case 'paruvendu':
                return 'Paru vendu';
                break;
            case 'lesiteimmo':
                return 'le site immo';
                break;
            case 'greenacres':
                return 'Green-Acres';
                break;
            case 'immonot':
                return 'Immo not';
                break;
            case 'etreproprio':
                return 'etre proprio';
                break;
            case 'vivastreet':
                return 'viva street';
                break;
            case 'immoregion':
                return 'immo region';
                break;
            case 'domimmo':
                return 'DOMimmo';
                break;
            case 'bureauxlocaux':
                return 'BureauxLocaux';
                break;
            case 'luxresidence':
                return 'Lux Residence';
                break;
            case 'surfaceprivee':
                return 'Surface privée';
                break;
            case 'residencesimmobilier':
                return 'Residences immobilier';
                break;
            case 'adaptimmobilier':
                return 'Adaptimmobilier';
                break;
            case 'topannonces':
                return 'TopAnnonces';
                break;
            case 'optimhome':
                return 'Optimhome';
                break;
            case 'era':
                return 'ERA';
                break;
            case 'proprietesprivees':
                return 'Proprietes privees';
                break;
            case 'ladresse':
                return 'L\'Adresse';
                break;
            case 'arthurimmo':
                return 'Arthurimmo';
                break;
            case 'megagence':
                return 'megAgence';
                break;
            case 'properstar':
                return 'Properstar';
                break;
            case 'luxuryestate':
                return 'LuxuryEstate';
                break;
            case 'espacesatypiques':
                return 'Espaces Atypiques';
                break;
            case 'pietrapolis':
                return 'Pietrapolis';
                break;
            case 'sixiemeavenue':
                return 'Sixime avenue';
                break;
        }
        return slug;
    }

    findSmallLink(ad) {
        var id = 0;
        var score = 25;
        var order = {
            'hektor': 1,
            'partagimmo': 2,
            'win': 3,
            'adaptimmo': 4,
            'netty': 5,
            'apimo': 6,
            'ubiflow': 7,
            'bellesdemeures': 10,
            'fnaim': 11,
            'pap': 12,
            'avendrealouer': 13,
            'logicimmo': 14,
            'foncia': 15,
            'gensdeconfiance': 16,
            'iad': 17,
            'century21': 18,
            'laforet': 19,
            'immonotaires': 20,
            'orpi': 21,
            'guyhoquet': 22,
            'efficity': 23,
            'stephaneplaza': 24,
            'safti': 25,
            'squarehabitat': 25,
            'citya': 26,
            'bskimmobilier': 27,
            'nestenn': 28,
            'human': 29,
            'capifrance': 30,
            'entreparticuliers': 31,
            'lesiteimmo': 32,
            'greenacres': 33,
            'immonot': 34,
            'etreproprio': 35,
            'vivastreet': 36,
            'immoregion': 37,
            'domimmo': 38,
            'bureauxlocaux': 39,
            'luxresidence': 40,
            'surfaceprivee': 41,
            'residencesimmobilier': 42,
            'adaptimmobilier': 43,
            'topannonces': 44,
            'optimhome': 45,
            'era': 46,
            'proprietesprivees': 47,
            'ladresse': 48,
            'arthurimmo': 49,
            'megagence': 50,
            'properstar': 51,
            'luxuryestate': 52,
            'espacesatypiques': 53,
            'pietrapolis': 54,
            'sixiemeavenue': 55,
            'ouestfrance': 100,
            'aucoeurdelimmo': 101,
            'maisonsetappartements': 102,
            'figimmo': 103,
            'seloger': 104,
            'bienici': 105,
            'leboncoin': 106
        };

        ad.urls.forEach((url, i) => {
            if (order[url.provider] !== undefined) {
                if (order[url.provider] < score) {
                    id = i;
                    score = order[url.provider];
                    return;
                }
            }
        });
        return id;
    }

    isAdVisible(favorite, index) {
        if (index == 0) {
            return true;
        }

        if (favorite.ad.ai_id == 0) {
            return true;
        }

        if (this.authenticationService.currentUser.pro_plan != '' || (this.authenticationService.currentUser.plan != '' && this.authenticationService.currentUser.plan != 'trial')) {
            return true;
        }
        return favorite.efficiency_brut < (favorite.ad_city.renta['R'] * 100);
    }

    verifSquare(square, squareLimit: SquareLimit) {
        if (square < squareLimit.square_q250 || square > squareLimit.square_q975) {
            return false;
        }
        return true;
    }

    isSquareValid(favorite) {
        if (favorite.ad_city.square_limit && favorite.estate_type == 'Parking') {
            if (favorite.ad_city.square_limit.P) {
                return this.verifSquare(favorite.square, favorite.ad_city.square_limit.P);
            }
        } else if (favorite.ad_city.square_limit_nb_piece && favorite.estate_type == 'Appartement') {
            if (favorite.rooms == 1) {
                if (favorite.ad_city.square_limit_nb_piece.A && favorite.ad_city.square_limit_nb_piece.A[1]) {
                    return this.verifSquare(favorite.square, favorite.ad_city.square_limit_nb_piece.A[1]);
                }
            } else if (favorite.rooms == 2) {
                if (favorite.ad_city.square_limit_nb_piece.A && favorite.ad_city.square_limit_nb_piece.A[2]) {
                    return this.verifSquare(favorite.square, favorite.ad_city.square_limit_nb_piece.A[2]);
                }
            } else if (favorite.rooms == 3) {
                if (favorite.ad_city.square_limit_nb_piece.A && favorite.ad_city.square_limit_nb_piece.A[3]) {
                    return this.verifSquare(favorite.square, favorite.ad_city.square_limit_nb_piece.A[3]);
                }
            } else if (favorite.rooms == 4) {
                if (favorite.ad_city.square_limit_nb_piece.A && favorite.ad_city.square_limit_nb_piece.A[4]) {
                    return this.verifSquare(favorite.square, favorite.ad_city.square_limit_nb_piece.A[4]);
                }
            } else if (favorite.rooms >= 5) {
                if (favorite.ad_city.square_limit_nb_piece.A && favorite.ad_city.square_limit_nb_piece.A[5]) {
                    return this.verifSquare(favorite.square, favorite.ad_city.square_limit_nb_piece.A[5]);
                }
            }
        } else if (favorite.ad_city.square_limit_nb_piece && favorite.estate_type == 'Maison') {
            if (favorite.rooms == 1) {
                if (favorite.ad_city.square_limit_nb_piece.M && favorite.ad_city.square_limit_nb_piece.M[1]) {
                    return this.verifSquare(favorite.square, favorite.ad_city.square_limit_nb_piece.M[1]);
                }
            } else if (favorite.rooms == 2) {
                if (favorite.ad_city.square_limit_nb_piece.M && favorite.ad_city.square_limit_nb_piece.M[2]) {
                    return this.verifSquare(favorite.square, favorite.ad_city.square_limit_nb_piece.M[2]);
                }
            } else if (favorite.rooms == 3) {
                if (favorite.ad_city.square_limit_nb_piece.M && favorite.ad_city.square_limit_nb_piece.M[3]) {
                    return this.verifSquare(favorite.square, favorite.ad_city.square_limit_nb_piece.M[3]);
                }
            } else if (favorite.rooms == 4) {
                if (favorite.ad_city.square_limit_nb_piece.M && favorite.ad_city.square_limit_nb_piece.M[4]) {
                    return this.verifSquare(favorite.square, favorite.ad_city.square_limit_nb_piece.M[4]);
                }
            } else if (favorite.rooms >= 5) {
                if (favorite.ad_city.square_limit_nb_piece.M && favorite.ad_city.square_limit_nb_piece.M[5]) {
                    return this.verifSquare(favorite.square, favorite.ad_city.square_limit_nb_piece.M[5]);
                }
            }
        } else if (favorite.ad_city.square_limit && favorite.estate_type == 'Immeuble') {
            if (favorite.ad_city.square_limit.I) {
                return this.verifSquare(favorite.square, favorite.ad_city.square_limit.I);
            }
        }
        return true;
    }
}

